import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';
import cn from 'classnames';

import { actions as countryDetailsActions } from '../../../redux/data/countryDetails';
import { selectEditedUserErrors } from '../../../redux/profiles/selectors';
import { useCountrySelect } from '../../../hocs/useCountrySelect';
import { COMMON } from '../../../constants/strings';
import { useLoggedUserCountry } from '../../DependentForm/utils/useLoggedUserCountry';
import EditBtn from '../../Button/EditBtn';
import Button from '../../Button';
import { getFieldsHomeAddressSection } from './getFieldsHomeAddressSection';
import { HomeAddressFormSection } from './HomeAddressFormSection';
import { useHomeAddressFormState } from './useHomeAddressFormState';
import { makeInitialValue } from './utils';
import styles from './styles.styl';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';

export function HomeAddress({
  formatMessage,
  canEdit,
  profile,
  countryList,
  saveUser,
  isSucceedUpdate,
}) {
  const [isEdit, toggleEdit] = useState(false);
  const {
    stateList,
    zipLimit,
    isZipApplicable,
    zipExamples,
  } = useCountrySelect(formatMessage);
  const errors = useSelector(selectEditedUserErrors);

  const dispatch = useDispatch();

  const fields = getFieldsHomeAddressSection(formatMessage, profile);

  const targetCountryEntity = useLoggedUserCountry();

  useEffect(() => {
    if (targetCountryEntity)
      dispatch(countryDetailsActions.send({ code: targetCountryEntity.value }));
  }, [targetCountryEntity]);

  useEffect(() => {
    if (isEdit) toggleEdit(!isSucceedUpdate);
  }, [isSucceedUpdate]);

  const {
    handleFormChange,
    validateErrors,
    setCountryValue,
    clearErrors,
    formState,
    isValid,
  } = useHomeAddressFormState(
    makeInitialValue(profile, countryList, stateList),
    errors,
  );

  const homePhoneValue = formState[PROFILE_FIELDS_TYPES.HOME_PHONE];

  const onCountryChange = data => {
    clearErrors();
    dispatch(countryDetailsActions.send({ code: data.value }));
    setCountryValue(data);
  };

  const isHomePhoneValid = homePhoneValue ? homePhoneValue.length >= 8 : true;

  const handleSubmit = e => {
    e.preventDefault();

    const fullCountryName = get(formState, 'country.label', '');
    const fullStateName = get(formState, 'state.label', '');

    clearErrors();

    saveUser({
      ...formState,
      country: fullCountryName,
      state: fullStateName,
      id: profile.id,
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.titleWrapper}>
        <h3 className={styles.title}>
          {formatMessage({ id: 'profile.section.address' })}
        </h3>
        {canEdit && (
          <div className={styles.editButton}>
            <EditBtn
              onClick={() => {
                handleFormChange(
                  makeInitialValue(profile, countryList, stateList),
                );
                toggleEdit(!isEdit);
              }}
            />
          </div>
        )}
      </div>
      {isEdit && (
        <HomeAddressFormSection
          {...formState}
          formatMessage={formatMessage}
          profile={profile}
          onChange={handleFormChange}
          isZipApplicable={isZipApplicable}
          onCountryChange={onCountryChange}
          statesList={stateList}
          countryList={countryList}
          validateErrors={validateErrors}
          zipLimit={zipLimit}
          zipExamples={zipExamples}
          targetCountryEntity={targetCountryEntity}
        />
      )}
      {!isEdit && (
        <div className={cn(styles.fieldsetContent, styles.withColumns)}>
          {fields &&
            fields.map(({ id, value, name }) => (
              <div className={styles.fieldWrapper} key={id}>
                <div className={styles.titleEntity}>{name}</div>
                <div className={styles.contentEntity}>{value || '-'}</div>
              </div>
            ))}
        </div>
      )}
      {isEdit && (
        <Fragment>
          <Button
            className="mt3 mr2 user-profile-save-button"
            raised
            color="primary"
            onClick={handleSubmit}
            disabled={!isValid || !isHomePhoneValid}
          >
            <FormattedMessage {...COMMON.save} />
          </Button>
          <Button
            className="mt3 user-profile-cancel-button"
            raised
            color="accent"
            onClick={() => {
              clearErrors();
              handleFormChange(
                makeInitialValue(profile, countryList, stateList),
              );
              toggleEdit(false);
            }}
          >
            <FormattedMessage {...COMMON.cancel} />
          </Button>
        </Fragment>
      )}
    </div>
  );
}

HomeAddress.propTypes = {
  formatMessage: PropTypes.func,
  canEdit: PropTypes.bool,
  profile: PropTypes.object,
  countryList: PropTypes.array,
  saveUser: PropTypes.func,
  isSucceedUpdate: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/HomeAddress/index.jsx